/**
 * What the capacity figure assumes — open on the page, not folded away.
 *
 * ⚠️ HIRES PER MONTH IS THE HEADLINE, and it is the only speed control. There used to be two: a
 * per-cluster HPM the sheet maintained, plus a global "efficiency gain" slider that scaled it. Two
 * knobs for one quantity, and the question anyone actually argues about — what do we think a
 * recruiter delivers in a month — was the one you could not answer directly. A team getting faster
 * is a higher HPM. The slider is gone.
 *
 * ⚠️ THE MEASURED RATE SITS BESIDE THE ESTIMATE. Every figure here is the team's own estimate, and
 * an estimate you cannot check against what happened is a number people either believe or ignore.
 * `actual` is this trimester's own hires ÷ FTE ÷ months elapsed — so a cluster assuming 2.5 and
 * running at 1.1 says so on the same line.
 */
import { cn } from '../../../../../shared';
import { CLUSTER_KEYS, type ClusterKey } from '../../../types/taTeam';
import type { CapacityAssumptions } from '../../../utils/taCapacityModel';
import { Section, WhatThisCounts } from '../tracking/trackingUi';

const FIELD =
  'h-9 w-[84px] rounded-md border border-solid border-gray-300 bg-white px-2 text-right text-sm ' +
  'tabular-nums text-gray-900 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500';

export function Assumptions({
  assumptions,
  actualHpmByCluster,
  fteByCluster,
  onChange,
  onReset,
  edited,
}: {
  assumptions: CapacityAssumptions;
  /** Null where there is nothing to divide by — no recruiters, or no trimester elapsed yet. */
  actualHpmByCluster: Record<ClusterKey, number | null>;
  fteByCluster: Record<ClusterKey, number>;
  onChange: (next: CapacityAssumptions) => void;
  onReset: () => void;
  edited: boolean;
}) {
  const num = (v: string) => (v === '' ? 0 : Number(v));

  return (
    <Section
      title="Assumptions"
      aside={
        <span className="flex items-center gap-2 text-xs text-gray-500">
          the team&apos;s own estimates, not measurements
          {edited ? (
            <button
              type="button"
              onClick={onReset}
              className="font-medium text-blue-600 underline underline-offset-2 hover:text-blue-700"
            >
              Reset
            </button>
          ) : null}
        </span>
      }
    >
      <div className="rounded-lg border border-gray-200">
        <div className="flex items-baseline gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2">
          <h3 className="text-sm font-semibold text-gray-900">Hires per month, per recruiter FTE</h3>
          <WhatThisCounts
            label="Hires per month"
            why="How many hires one recruiter FTE lands in a month in this cluster. It is the number the whole capacity figure multiplies out from: capacity = (recruiter FTE + intern FTE × intern productivity) × HPM × months. Actual is this trimester's own offers accepted ÷ that cluster's recruiter FTE ÷ the months gone so far."
          />
        </div>
        <table className="w-full">
          <thead>
            <tr className="text-xs font-medium text-gray-500">
              <th className="px-3 py-1.5 text-left">Cluster</th>
              <th className="px-3 py-1.5 text-right">Recruiter FTE</th>
              <th className="px-3 py-1.5 text-right">Assumed</th>
              <th className="px-3 py-1.5 text-right">Actual so far</th>
            </tr>
          </thead>
          <tbody>
            {CLUSTER_KEYS.map((key) => {
              const actual = actualHpmByCluster[key];
              const assumed = assumptions.hpm[key] ?? 0;
              /*
               * Flagged only when the estimate is optimistic by a clear margin, and only once the
               * trimester has produced a rate at all. An early trimester is noisy by construction —
               * one hire in week two is not a trend — so this is a nudge to look, not a verdict.
               */
              const optimistic = actual !== null && assumed > 0 && actual < assumed * 0.7;
              return (
                <tr key={key} className="border-t border-gray-100 text-sm">
                  <td className="px-3 py-2 font-medium text-gray-900">{key}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                    {(fteByCluster[key] ?? 0).toFixed(2)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <input
                      type="number"
                      min={0}
                      step={0.1}
                      aria-label={`Hires per month assumed for ${key}`}
                      value={String(assumed)}
                      onChange={(e) =>
                        onChange({
                          ...assumptions,
                          hpm: { ...assumptions.hpm, [key]: num(e.target.value) },
                        })
                      }
                      className={FIELD}
                    />
                  </td>
                  <td
                    data-testid={`hpm-actual-${key}`}
                    className={cn(
                      'px-3 py-2 text-right tabular-nums',
                      optimistic ? 'font-semibold text-amber-700' : 'text-gray-600',
                    )}
                  >
                    {actual === null ? '—' : actual.toFixed(2)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex flex-wrap items-end gap-x-6 gap-y-3">
        <Field
          label="Intern productivity"
          unit="×"
          step={0.05}
          value={assumptions.internProductivity}
          onChange={(v) => onChange({ ...assumptions, internProductivity: v })}
          why="What one intern FTE delivers against one recruiter FTE. 0.5 means an intern at full time contributes what half a recruiter does."
        />
        <Field
          label="Backfills not yet raised"
          unit="roles"
          step={1}
          value={assumptions.unraisedBackfills}
          onChange={(v) => onChange({ ...assumptions, unraisedBackfills: v })}
          why="Backfills you expect this trimester that nobody has entered in the Hiring Database yet. ZERO by default on purpose: every backfill that exists is already a row in the HDB and counted in the demand above, so a non-zero default would count the raised ones twice. Set it when you believe leavers are coming that the plan has not caught up with."
        />
        <Field
          label="Full-time week"
          unit="h"
          step={1}
          value={assumptions.fullWeekHours}
          onChange={(v) => onChange({ ...assumptions, fullWeekHours: v })}
          why="What 1.0 FTE means in contracted hours. Every person's FTE on the team above is their hours divided by this."
        />
      </div>
    </Section>
  );
}

function Field({
  label,
  unit,
  step,
  value,
  onChange,
  why,
}: {
  label: string;
  unit: string;
  step: number;
  value: number;
  onChange: (v: number) => void;
  why: string;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="flex items-center gap-1 text-xs font-medium text-gray-500">
        {label} <span className="text-gray-400">({unit})</span>
        <WhatThisCounts label={label} why={why} />
      </span>
      <input
        type="number"
        min={0}
        step={step}
        aria-label={`${label} in ${unit}`}
        value={String(value)}
        onChange={(e) => onChange(e.target.value === '' ? 0 : Number(e.target.value))}
        className={FIELD}
      />
    </label>
  );
}
